import { Icon } from '@chakra-ui/react';
import {
  BasePagination,
  Box,
  Flex,
  IconButton,
  IconButtonWithTooltip,
  Link,
  PenIcon,
  Text,
  useBreakpointValue,
} from '@elkaso-app/web-design';
import { useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import {
  MasterProductFamily,
  useDownloadMasterProductFamiliesSheetApi,
  useGetMasterProductFamiliesApi,
} from 'apis/master-product-families';
import { BaseTableContainer, ExcelTable } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { MergeFamilyModal } from 'pages/inquiry-processor/components/family-workspace/merge-family-modal';
import { CreateFamilyModal } from 'pages/inquiry-processor/components/master-product-families/create-family-modal';
import {
  DeleteFamilyModal,
  IDeleteFamilyModalCell,
} from 'pages/inquiry-processor/components/master-product-families/delete-family-modal';
import {
  EditFamilyModal,
  IEditFamilyModalCell,
} from 'pages/inquiry-processor/components/master-product-families/edit-family-modal';
import { EditableFamiliesGrid } from 'pages/inquiry-processor/components/master-product-families/editable-families-grid/editable-families-grid';
import { FamilyListCard } from 'pages/inquiry-processor/components/master-product-families/family-list-card';
import { FamilyCategoryFilterButton } from 'pages/inquiry-processor/components/master-product-families/filters/category-filter';
import { UniversalNameFilterButton } from 'pages/inquiry-processor/components/master-product-families/filters/universal-name-filter';
import {
  FamilyCategoryCell,
  IFamilyCategoryCell,
} from 'pages/inquiry-processor/components/master-product-families/table-cells/family-category-cell';
import {
  FamilyDateCell,
  IFamilyDateCell,
} from 'pages/inquiry-processor/components/master-product-families/table-cells/family-date-cell';
import {
  FamilyPhotoCell,
  IFamilyPhotoCell,
} from 'pages/inquiry-processor/components/master-product-families/table-cells/family-photo-cell';
import { UploadFamiliesSheetModal } from 'pages/inquiry-processor/components/master-product-families/upload-families-sheet-modal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiDownload, FiEdit, FiGitMerge, FiList, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const MasterProductFamiliesScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const setPaginationParams = useSetPaginationParamsV1();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [mode, setMode] = useState<'read' | 'edit'>('read');
  const [mergeSourceFamily, setMergeSourceFamily] = useState<MasterProductFamily | null>(null);

  const openFamilyWorkspace = useCallback(
    (familyId: number) => history.push(`${match.url}/${familyId}`),
    [history, match.url]
  );

  useEffect(() => {
    dispatch(changeTitle('GPO — Master Products'));
  }, [dispatch]);

  const { data, isLoading, isFetching } = useGetMasterProductFamiliesApi();
  const { refetch: downloadSheet, isFetching: isDownloading } = useDownloadMasterProductFamiliesSheetApi();
  const { data: categoryTreeData } = useGetDealCategoryTreeApi();
  const categoryTree = categoryTreeData?.data ?? [];

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Universal Name' filterButtonComponent={<UniversalNameFilterButton />} />,
        accessor: 'universalName',
        width: 250,
        Cell: ({ row }: CellProps<any, any>) => (
          <Link onClick={() => openFamilyWorkspace(row.original.id)} _hover={{ textDecoration: 'underline' }}>
            <Text as='span' color='red.500' fontWeight='500'>
              {row.original.universalName}
            </Text>
          </Link>
        ),
      },
      {
        Header: <TableHeader name='Photo' />,
        accessor: 'imageUrl',
        width: 80,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IFamilyPhotoCell> as={FamilyPhotoCell} imageUrl={row.original.imageUrl} />
        ),
      },
      {
        Header: <TableHeader name='Category' filterButtonComponent={<FamilyCategoryFilterButton />} />,
        accessor: 'catalogCategoryId',
        width: 240,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IFamilyCategoryCell>
            as={FamilyCategoryCell}
            catalogCategoryId={row.original.catalogCategoryId}
            categoryTree={categoryTree}
          />
        ),
      },
      {
        Header: <TableHeader name='Created & Updated' />,
        accessor: 'createdAt',
        width: 200,
        Cell: ({ row }: CellProps<any, any>) => (
          <CustomTableCell<IFamilyDateCell>
            as={FamilyDateCell}
            createdAt={row.original.createdAt}
            updatedAt={row.original.updatedAt}
          />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'id',
        width: 150,
        Cell: ({ row }: CellProps<any, any>) => {
          const family = row.original as MasterProductFamily;
          return (
            <>
              <CustomTableCell<IEditFamilyModalCell>
                as={EditFamilyModal}
                family={family}
                trigger={
                  <IconButtonWithTooltip
                    size='sm'
                    label='Edit'
                    aria-label='edit-family-btn'
                    Icon={<PenIcon />}
                    variant='unstyled'
                  />
                }
              />
              <CustomTableCell<IDeleteFamilyModalCell>
                as={DeleteFamilyModal}
                family={family}
                trigger={
                  <IconButtonWithTooltip
                    size='sm'
                    label='Delete'
                    aria-label='delete-family-btn'
                    Icon={<Icon as={FiTrash2} />}
                    variant='unstyled'
                  />
                }
              />
              <IconButtonWithTooltip
                size='sm'
                label='Open variants'
                aria-label='view-variants-btn'
                Icon={<Icon as={FiList} />}
                variant='unstyled'
                onClick={() => openFamilyWorkspace(family.id)}
              />
              <IconButtonWithTooltip
                size='sm'
                label='Merge into…'
                aria-label='merge-family-btn'
                Icon={<Icon as={FiGitMerge} />}
                variant='unstyled'
                onClick={() => setMergeSourceFamily(family)}
              />
            </>
          );
        },
      },
    ],
    [categoryTree, openFamilyWorkspace]
  );

  const tableData = useMemo(() => data?.data || [], [data?.data]);

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Master Products' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          {mode === 'read' && (
            <>
              <IconButtonWithTooltip
                aria-label='Download Sheet'
                label='Download Sheet'
                Icon={<Icon as={FiDownload} />}
                isLoading={isDownloading}
                onClick={() => downloadSheet()}
              />
              <UploadFamiliesSheetModal
                trigger={
                  <IconButtonWithTooltip aria-label='Upload Sheet' label='Upload Sheet' Icon={<Icon as={FiUpload} />} />
                }
              />
              <IconButtonWithTooltip
                aria-label='Bulk Edit'
                label='Bulk Edit'
                Icon={<Icon as={FiEdit} />}
                isDisabled={isLoading}
                onClick={() => setMode('edit')}
              />
              <IconButton
                aria-label='Add Family'
                icon={<FiPlus />}
                colorScheme='red'
                onClick={() => setIsCreateOpen(true)}
              />
              <ClearFilterButton />
            </>
          )}
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      {mode === 'read' && isMobile ? (
        // Cards live outside TableWrapper: TableContainer's whiteSpace='nowrap' would leak into card text.
        <Box flexGrow={1} overflowY='auto' minH='200px' px='sm' pb='sm'>
          <Flex flexWrap='wrap' gap='4px' align='center' mb='10px'>
            <UniversalNameFilterButton />
            <FamilyCategoryFilterButton />
          </Flex>
          {tableData.length === 0 ? (
            <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' p='14px'>
              <Text fontSize='13px' color='gray.500' textAlign='center'>
                No families found
              </Text>
            </Box>
          ) : (
            tableData.map((family: MasterProductFamily) => (
              <FamilyListCard
                key={family.id}
                family={family}
                categoryTree={categoryTree}
                onOpen={() => openFamilyWorkspace(family.id)}
                onMerge={() => setMergeSourceFamily(family)}
              />
            ))
          )}
        </Box>
      ) : (
        <BaseTableContainer.TableWrapper>
          {mode === 'edit' ? (
            <EditableFamiliesGrid families={tableData} onDone={() => setMode('read')} />
          ) : (
            <ExcelTable columns={columns} data={tableData} />
          )}
        </BaseTableContainer.TableWrapper>
      )}

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          {mode === 'read' && (
            <BasePagination>
              <BasePagination.Pages pagesCount={data?.pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={data?.total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          )}
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>

      <CreateFamilyModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(family) => openFamilyWorkspace(family.id)}
      />

      {mergeSourceFamily && (
        <MergeFamilyModal
          isOpen
          onClose={() => setMergeSourceFamily(null)}
          sourceFamily={{ id: mergeSourceFamily.id, universalName: mergeSourceFamily.universalName }}
        />
      )}
    </BaseTableContainer>
  );
};
